import React, { useState, useEffect, useRef, useMemo, use } from "react";
import { Box, Typography, IconButton, Dialog } from "@mui/material";
import {
  LuRefreshCw,
  FiFileText,
  LuChevronDown,
  LuCopy,
  LuFileMinus,
  LuPlus,
  LuArrowLeft,
} from "@/Components/Icons/Icons";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import { navurl } from "@/Config/Routeconfig/NavigationUrl";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import { Menu, MenuItem } from "@mui/material";
import { getAllNamesFromMultipleSchemas } from "@/Utils/CsvColumns";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "@/Context/UserContext";
import { SectionHeader } from "@/Components/Header/SectionsHeader";
import { hookMap } from "@/redux/hookMap";
import { BiSolidError } from "react-icons/bi";
import { iconMap } from "@/redux/iconMap";
import { TabWithIndependentButtons } from "@/Components/Tabs/SubTabs";
import { useLazyGetExportGeneralByIdQuery } from "@/redux/ExportPage/Dashboard/ExportIdSlice";
import { useInvoice } from "@/Context/InvoiceContext";
import CreateFilterBox from "@/Views/ImportJob/CreateNewJob/FilterBox";
import FormGenerator from "@/Utils/FormGenerator/FormGenerator";
import SuccessDialog from "@/Components/Dialog/SuccessDialog";
import CreateNewJobSidebarItems from "@/Config/Export/CreateNewJob/CreateNewjobsidebar";
import TableGenerator from "@/Utils/FormGenerator/TableGenerator";
import CustomTabs from "@/Components/Tabs/CustomTab";
import TableWithFieldGenerator from "@/Utils/FormGenerator/TableWithFieldGenerator";
import FormDialog from "@/Components/Dialog/FormDialog";
import CheckboxBox from "@/Components/Form/CheckboxBox";
import SummaryPopover from "@/Components/Dialog/SummaryPopover";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import Papa from "papaparse";
import CreateJobLayout from "@/Views/ImportJob/CreateNewJob/CreateNewJobLayout";
import schema1 from "@/Config/Export/CreateNewJob/GeneralDetailsSchema";
import schema2 from "@/Config/Export/CreateNewJob/Entity/EntityCustomizedList";
import { dynamicComponentMap } from "@/Utils/DynamicComponents";
import { LuCheck } from "react-icons/lu";
import { applyDynamicRules } from "@/Utils/Rules/ApplyConditions";
import { ImportCreateNewJobSidebarItems } from "@/Config/ImportJob/CreateNewJob/CreateNewJobSideBar";
import { importHookMap } from "@/redux/importHookMap";
import { useLazyGetImportGeneralByIdQuery } from "@/redux/ImportPage/CreateNewJob/General/GeneralApiSlice";

const CreateNewJob = () => {
  const formRef = useRef();
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeSubIndex, setActiveSubIndex] = useState(0);
  const [activeNestedIndex, setActiveNestedIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const {
    clearAll,
    isRequiredFieldFilled,
    setIsRequiredFieldFilled,
    loading,
    setLoading,
    formData,
    setFormData,
    setIsDataFetched,
    isDataFetched,
    setImporterMasterId,
    importerMasterId,
    setValueGlobal,
  } = useUser();

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [subDialogConfig, setSubDialogConfig] = useState(false);
  const [subDialogOpen, setSubDialogOpen] = useState(false);

  const [processingId, setProcessingId] = useState(null);
  const [formDialogData, setFormDialogData] = useState({});
  const [schema, setSchema] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [dialogOwnerConfig, setDialogOwnerConfig] = useState(null);
  const { importerId } = useParams();
  const [disableCopy, setDisableCopy] = useState(false);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [openComponent, setOpenComponent] = useState(null);
  const { importTrigger, setImportTrigger } = useUser();
  const [extraApi, setExtraApi] = useState(null);
  const isEditMode = Boolean(importerId);
  const isActivation = Boolean(formData?.general?.importJobNumber);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [isCopyMode, setIsCopyMode] = useState(false);

  useEffect(() => {
    clearAll();
  }, []);

  useEffect(() => {
    setImporterMasterId(importerId);
  }, [importerId]);

  const initialItems = useMemo(
    () => ImportCreateNewJobSidebarItems(formData),
    [formData]
  );

  // 1️⃣ Flatten all APIs from nested sidebar items
  const collectApiKeys = (items) => {
    let keys = [];
    items.forEach((item) => {
      if (item.api) keys.push(item.api);
      if (item.tableApi) keys.push(item.tableApi);
      if (item.extraApi) keys.push(item.extraApi);
      if (item.subTabs) keys.push(...collectApiKeys(item.subTabs));
    });
    return keys;
  };

  // 2️⃣ Custom hook to call all API hooks safely
  const useSidebarApiHooks = (items) => {
    const allKeys = Array.from(new Set(collectApiKeys(items))); // unique
    const results = allKeys.map((key) => {
      const hook = importHookMap[key];
      return hook ? hook() : null; // ✅ called unconditionally
    });

    return allKeys.reduce((acc, key, i) => {
      acc[key] = results[i];
      return acc;
    }, {});
  };
  dayjs.extend(utc);


  const normalizeDatesDeep = (data) => {
    if (Array.isArray(data)) {
      return data.map(normalizeDatesDeep);
    }
  
    if (data && typeof data === "object") {
      return Object.fromEntries(
        Object.entries(data).map(([key, value]) => {
  
          // ✅ DD-MM-YYYY
          if (typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
            return [
              key,
              dayjs.utc(value, "DD-MM-YYYY").startOf("day").toISOString(),
            ];
          }
  
          // ✅ DD-MM-YYYY HH:mm:ss
          if (
            typeof value === "string" &&
            /^\d{2}-\d{2}-\d{4}\s\d{2}:\d{2}:\d{2}$/.test(value)
          ) {
            return [
              key,
              dayjs.utc(value, "DD-MM-YYYY HH:mm:ss").toISOString(),
            ];
          }
  
          return [key, normalizeDatesDeep(value)];
        })
      );
    }
  
    return data;
  };

  const cleanCopyData = (data) => {
    return Object.fromEntries(
      Object.entries(data).filter(([key, value]) => {
        // ❌ remove UI-only / meta fields
        if (
          ["srNo", "__schemaKey", "__index", "rowIndex", "selected"].includes(key)
        ) {
          return false;
        }
  
        // ❌ remove keys ending with id / Id
        // BUT keep object values like { id, name }
        if (
          /id$/i.test(key) &&
          !(
            typeof value === "object" &&
            value !== null &&
            "id" in value &&
            "name" in value
          )
        ) {
          return false;
        }
  
        return true; // ✅ keep everything else
      })
    );
  };
  
  
  const apiHooks = useSidebarApiHooks(initialItems);

  // Update sidebar items dynamically when formData changes
  useEffect(() => {
    const updatedItems = applyDynamicRules(initialItems, formData);
    setSidebarItems(updatedItems);

    const currentItem = updatedItems[activeIndex];
    if (currentItem?.disabled) {
      const firstEnabledIndex = updatedItems.findIndex(
        (item) => !item.disabled
      );
      if (firstEnabledIndex !== -1) setActiveIndex(firstEnabledIndex);
    }
  }, [formData]);

  const openForm = (schema, label, ownerConfig = null, extraApi = null) => {
    setFormDialogData({});
    setIsEditing(false);
    setShowForm(true);
    setExtraApi(extraApi);
    setSchema({ schema, label });
    if (ownerConfig) {
      setDialogOwnerConfig(ownerConfig);
    }
  };

  const jsonSchemaColumns = getAllNamesFromMultipleSchemas([schema1, schema2]);

  const navigate = useNavigate();

  const fileInputRef = useRef();

  const handleImportClick = () => {
    // localStorage.removeItem("exporterMasterId");
    navigate("/import/job", { replace: true });
    setTimeout(() => {
      fileInputRef.current.click();
    }, 100);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const csvData = results.data;

        // Base object with all schema columns empty
        const baseObject = {};
        jsonSchemaColumns.forEach((col) => {
          baseObject[col] = "";
        });

        // Merge CSV row values into base object
        const mappedData = csvData.map((row) => {
          const newRow = { ...baseObject };
          jsonSchemaColumns.forEach((col) => {
            if (row[col] !== undefined) newRow[col] = row[col];
          });
          return newRow;
        });

        // Form ku first row
        const finalData = mappedData[0] || baseObject;
        setFormData(finalData);

        // Table ku all rows + index
        const tableWithIndex = mappedData.map((row, idx) => ({
          id: idx + 1,
          ...row,
        }));
        setTableData(tableWithIndex);
      },
      error: (err) => console.error("CSV parse error:", err),
    });
  };

  const goToCreateNewJob = async () => {
    try {
      // Step 1: Show loader
      setLoading(true);

      // Step 2: Clear data
      await new Promise((resolve) => {
        clearAll({});
        setTimeout(resolve, 800); // small delay to let context clear
      });

      // Step 3: Set states
      setIsDataFetched(true);
      setDisableCopy(true);

      // Step 4: Navigate
      navigate("/import/job");
    } catch (err) {
      console.error("Error while creating new job:", err);
    } finally {
      // Step 5: Hide loader
      setLoading(false);
    }
  };

  const copyJobData = () => {
    navigate("/import/job");
  };

  const handleCloseForm = () => {
    setIsEditing(false);
    setFormDialogData({});
    setShowForm(false);
    setSubDialogOpen(false);
    setDialogOwnerConfig(null);
  };

  const handlers = {
    handleEdit: (row) => {
      if (!row) return;
      setFormDialogData(row);
      setIsEditing(true);
      setShowForm(true);
      setSchema({ schema: currentTab.schema, label: currentTab.id });
    },
    handleRemove: async (row) => {
      try {
        await handleDelete({ id: row.id });
      } catch (err) {
        console.error("Failed to delete:", err);
      }
    },
    handleRowClick: async (row) => {
      try {
        await handleRowSelect({ id: row.id });
      } catch (err) {
        console.error("Failed to select the row:", err);
      }
    },
    handleView: (row) => {
      if (!row) return;
      const fileObjectKey = Object.keys(row).find(
        (key) =>
          row[key] &&
          typeof row[key] === "object" &&
          row[key].filePath &&
          row[key].fileName
      );

      if (fileObjectKey) {
        const fileData = row[fileObjectKey];
        try {
          const fileUrl = `${import.meta.env.VITE_BASE_FILE_URL}${fileData.filePath}`;
          window.open(encodeURI(fileUrl), "_blank");
        } catch (err) {
          console.error("View failed:", err);
        }
        return;
      }
    },
  };

  const [fetchImportGeneral, { data, isFetching }] =
    useLazyGetImportGeneralByIdQuery();

  const handlerefresh = async (id = importerMasterId) => {
    try {
      setLoading(true);
      const result = await fetchImportGeneral({
        id: id,
      }).unwrap();

      if (!result) return;
      setFormData((prev) => ({
        ...prev,
        ["general"]: {
          ...(prev["general"] || {}),
          ...result,
        },
      }));
      setIsRequiredFieldFilled(true);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setSaveLoading(false);
      setIsDataFetched(true);
    }
  };

  const documentOptions = [
    "Shipping Bill",
    "Shipping Bill (GST)",
    "Annexure",
    "Invoice",
    "Packing List",
    "Certificate Of Origin",
    "GSP / ISFTA",
    "CheckList",
    "Goods Regn. Checklist",
    "N-Form",
    "GR-Form",
    "SEZ Submission",
    "ICEGATE Submission",
    "GR Submission",
    "BL Instruction",
  ];

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState("");
  const [anchorEl1, setAnchorEl1] = useState(null);

  const { invoiceId } = useInvoice();
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setAnchorEl1(null);
  };
  // const functionMap = {
  //   handleOpen: (event, btn) => {
  //     const button = event.target.closest("button");

  //     setAnchorEl1(button);
  //   },
  // };

  const functionMap = {
    handleOpen: (event, btn) => {
      const button = event.target.closest("button");
      setAnchorEl1(button);

      // If the JSON specifies a component to open, store it
      if (btn.component) {
        setOpenComponent(btn);
      }
    },

    handleClose: () => {
      setAnchorEl1(null);
      setOpenComponent(null);
    },
  };

  // const sidebarItems = CreateNewJobSidebarItems(formData);
  const activeItem = sidebarItems[activeIndex] ?? {};
  const activeSubTab =
    activeItem?.type === "tab"
      ? activeItem.subTabs?.find((subTab) => subTab.id === activeSubIndex)
      : null;
  const activeNestedTab =
    activeSubTab?.type === "tab"
      ? activeSubTab.subTabs[activeNestedIndex]
      : null;
  const currentTab = activeNestedTab || activeSubTab || activeItem;

  useEffect(() => {
    setActiveSubIndex(0);
    setActiveNestedIndex(0);
  }, [activeIndex, invoiceId]);

  useEffect(() => {
    setActiveNestedIndex(0);
  }, [activeSubIndex]);

  const handleSelect = (doc) => {
    setSelectedDoc(doc);
    handleClose();
  };

  useEffect(() => {
    if (formData?.general?.processingId) {
      setProcessingId(formData?.general?.processingId);
    }
  }, [formData]);

  useEffect(() => {
    let isMounted = true;

    // setSchema(null);
    const fetchTabData = async () => {
      const isTriggerRefresh =
        typeof importTrigger === "number" && importTrigger > 0;
      const sidebarItems = ImportCreateNewJobSidebarItems();
      const activeItem = sidebarItems[activeIndex];
      const activeSubTab =
        activeItem?.type === "tab"
          ? activeItem.subTabs?.find((subTab) => subTab.id === activeSubIndex)
          : null;
      const activeNestedTab =
        activeSubTab?.type === "tab"
          ? activeSubTab.subTabs[activeNestedIndex]
          : null;
      const config = activeNestedTab || activeSubTab || activeItem;

      try {
        if (!isTriggerRefresh) {
          setIsDataFetched(false);
          setLoading(true);
          setIsRequiredFieldFilled(false);
        }
        if (!config?.api && !config?.tableApi) return;

        const { fetch } = apiHooks[config.api] || {};
        if (!isMounted) return; // prevent stale updates

        const { fetchTable } = apiHooks[config.tableApi] || {};
        if (!fetch && !fetchTable) {
          console.warn("ℹ️ No API configured for:", config?.label);
          return;
        }

        let id = importerMasterId || importerId;

        if (config.replaceMasterId) {
          const data = formData[config.replaceMasterId];

          if (Array.isArray(data) && data.length > 0) {
            const selectedItem = data.find((item) => item.selected);
            id = selectedItem ? selectedItem.id : data[0].id;
          } else if (data && typeof data === "object") {
            id = data.id;
          }
        }

        let dataId = null;

        if (config.attachData) {
          const dataArray = formData[config.attachData] || [];
          if (Array.isArray(dataArray) && dataArray.length > 0) {
            // If something is selected, send its ID, else take the first item's ID
            const selectedItem = dataArray.find((item) => item.selected); // assuming `selected` flag
            dataId = selectedItem ? selectedItem.id : dataArray[0].id;
          }
        }

        let payload = { id };

        if (dataId) {
          payload.subId = dataId;
        }
        let response = null;
        if (fetch) {
          try {
            response = await fetch(payload);
          } catch (err) {
            console.error("⚠️ Failed to fetch main form data:", err);
            setFormData((prev) => ({
              ...prev,
              [config.type === "formWithTable"
                ? config.id + "Form"
                : config.id]: {}, // clear the specific tab data
            }));

            response = null; // gracefully continue
          }
        }

        let tableResponse;

        if (!["table", "tableWithFields"].includes(config.type)) {
          setFormData((prev) => ({
            ...prev,
            [config.type == "formWithTable" ? config.id + "Form" : config.id]: {
              ...(prev[
                config.type == "formWithTable" ? config.id + "Form" : config.id
              ] || {}),
              ...response,
            },
          }));
        }

        const selectedProduct = formData[config.tableReplaceMasterId]?.find(
          (product) => product.selected === true
        );

        const tableId =
          selectedProduct?.id ||
          (config.tableReplaceMasterId
            ? formData[config.tableReplaceMasterId]?.id ||
              response?.[`${config.tableReplaceMasterId}Id`]
            : importerMasterId || importerId);

        let tablePayload = { id: tableId };

        if (config.tableApi) {
          tableResponse = await fetchTable(tablePayload);
        }

        if (["table", "tableWithFields"].includes(config.type)) {
          // setFormData((prev) => ({
          //   ...prev, // keep previous keys
          //   [config.id]: response, // update/add the new key
          // }));
          setFormData((prev) => {
            //this set form
            const previousData = prev[config.id] || []; // e.g., invoiceMain
            let updatedData = response;
            if (Array.isArray(response) && response.length > 0) {
              // Try to preserve previously selected row
              const prevSelectedId = previousData.find(
                (row) => row.selected
              )?.id;

              if (prevSelectedId) {
                updatedData = response.map((row) => ({
                  ...row,
                  selected: row.id === prevSelectedId,
                }));
              } else {
                // No previous selection, default to first row
                updatedData = response.map((row, idx) => ({
                  ...row,
                  selected: idx === 0,
                }));
              }
            }

            return {
              //this
              ...prev,
              [config.id]: updatedData,
            };
          });
        } else if (config.type == "formWithTable") {
          setFormData((prev) => {
            // 🔒 Prevent redundant update that triggers re-render loop
            const prevData = prev[config.id] || [];
            if (JSON.stringify(prevData) === JSON.stringify(tableResponse)) {
              return prev; // no change, skip update
            }
            return {
              ...prev,
              [config.id]: tableResponse,
            };
          });
        }
      } catch (err) {
        setLoading(false);
        console.error("Failed to fetch tab data:", err);
        if (!["table", "tableWithFields"].includes(config.type)) {
          setFormData((prev) => ({
            ...prev,
            [config.type == "formWithTable" ? config.id + "Form" : config.id]:
              {},
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            [config.id]: [],
          }));
        }
      } finally {
        setLoading(false);
        setIsDataFetched(true);
        return () => {
          isMounted = false;
        };
      }
    };

    fetchTabData();

    return () => {
      isMounted = false;
    };
  }, [
    activeIndex,
    activeSubIndex,
    activeNestedIndex,
    invoiceId,
    importTrigger,
  ]);
  useEffect(() => {
    const data = formData[currentTab?.id];

    // ❌ not array → skip
    if (!Array.isArray(data)) return;

    // 🔥 find default selected row
    const defaultSelectedRow = data.find((r) => r?.selected === true);

    if (defaultSelectedRow) {
      setSelectedRowData(defaultSelectedRow);
      setIsRequiredFieldFilled(true);
    }
  }, [formData, currentTab?.id]);

  const handleRowSelect = async (rowObj, dataKey = currentTab.id) => {
    if (!rowObj?.id) {
      console.warn("⚠️ rowObj.id missing", rowObj);
      return;
    }

    const allRows = formData[dataKey] || [];

    // 🔥 FULL ROW
    const fullRow = allRows.find((r) => r.id === rowObj.id);

    if (!fullRow) {
      console.warn("❌ Full row not found for id:", rowObj.id);
      return;
    }
    // 🔥 save selected row
    setSelectedRowData(fullRow);

    // 🔥 update selection state in table
    setFormData((prev) => {
      const updated = {
        ...prev,
        [dataKey]: allRows.map((r) => ({
          ...r,
          selected: r.id === rowObj.id,
        })),
      };

      return updated;
    });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      if (!currentTab?.api) {
        console.warn("ℹ️ No API configured for:", currentTab?.label);
        return;
      }
      const deleteProcess = currentTab?.tableApi
        ? (apiHooks[currentTab.tableApi] || {}).deleteTableFn
        : (apiHooks[currentTab.api] || {}).deleteFn;

      var response = await deleteProcess?.(id).unwrap();

      setFormData((prev) => {
        // get existing array under a key, for example 'someKey'
        const existingArray = prev[currentTab?.id] || [];

        // filter out the item by id
        const updatedArray = existingArray.filter(
          (r) => String(r.id) !== String(id.id)
        );

        return {
          ...prev,
          [currentTab?.id]: updatedArray, // update the key with filtered array
        };
      });

      if (response?.data?.statusCode === 200 || response?.statusCode === 200) {
        setSuccessDialogConfig({
          label: "Success",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {response?.data?.message ||
                response?.message ||
                "Data deleted successfully"}
            </strong>
          ),
          buttonText: "OK",
          IconComponent: BiSolidError,
        });
        setShowSuccessDialog(true);
      } else {
        setSuccessDialogConfig({
          label: "Failure",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {response?.data?.message || "Something went wrong"}
            </strong>
          ),
          buttonText: "OK",
          status: "error",
          icon: BiSolidError,
        });
        setShowSuccessDialog(true);
      }
      return response;
    } catch (err) {
      console.error(err);
      setSuccessDialogConfig({
        label: "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {err?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        icon: BiSolidError,
      });
      setShowSuccessDialog(true);
      throw err;
    } finally {
      setLoading(false);
      setImportTrigger((prev) => prev + 1);
    }
  };
  //extraapi
  // 🧩 Generic Extra API Handler
  const extraHandleSave = async (extraApi, updatedData, name) => {
    try {
      if (!extraApi) return;

      const apiSet = apiHooks[extraApi];
      if (!apiSet) {
        console.warn(`⚠️ No API found for ${extraApi}`);
        return;
      }

      const { fetch, create, update } = apiSet;

      let extraResponse;

      let id = importerId;

      const cleanData = Object.fromEntries(
        Object.entries(updatedData || {}).filter(
          ([_, v]) => v != null && v !== ""
        )
      );
      // GET process with query params
      if (fetch) {
        let data = new URLSearchParams(cleanData).toString();
        extraResponse = await fetch({
          id,
          data,
        });
      }

      // CREATE process
      else if (create) {
        extraResponse = await create({ id: importerId, ...cleanData }).unwrap();
      }

      // UPDATE process
      else if (update) {
        extraResponse = await update({ id: importerId, ...cleanData }).unwrap();
      }

      setShowForm(false);
      return extraResponse;
    } catch (err) {
      console.error(`❌ Extra API failed for ${extraApi}:`, err);
    }
  };

  const flattenDataTable = (obj) => {
    if (obj === null || obj === undefined) return obj;

    const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

    // If obj is a string, check if it's an ISO date
    if (typeof obj === "string") {
      if (isoRegex.test(obj)) {
        const parsed = dayjs(obj);
        if (parsed.isValid()) return parsed.format("DD-MM-YYYY HH:mm:ss");
      }
      return obj;
    }

    // If obj is a number, boolean, or other primitive, return as is
    if (typeof obj !== "object") return obj;

    // If obj is an array, map recursively
    if (Array.isArray(obj)) {
      return obj.map((item) => flattenDataTable(item));
    }

    // Now obj is a plain object
    if ("name" in obj) return obj.name;
    if ("label" in obj) return obj.label;

    // Otherwise, recurse for nested objects
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        // If value is string and matches ISO, convert it
        if (typeof value === "string" && isoRegex.test(value)) {
          const parsed = dayjs(value);
          return [
            key,
            parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm:ss") : value,
          ];
        }

        // Otherwise, recurse
        return [key, flattenDataTable(value)];
      })
    );
  };

  const handleSave = async (type) => {
    const isCopy = isCopyMode === true;
    try {
      let filledData = {};

      if (formRef.current) {
        filledData = isCopy
          ? formRef.current.getCurrentValues() // 🔥 FULL DATA
          : await formRef.current.submit();    // 🧠 OLD FLOW
      }
      
      filledData=normalizeDatesDeep(filledData);
      if (!Object.keys(filledData).length) return;
      // 🧨 COPY MODE CLEANUP
      if (isCopy) {
        delete filledData.id;
        delete filledData.selectedId;
      }
      
      console.log("📝 [handleSave] filledData:", filledData);
      // 🟢 Prepare transformed data
      const updatedData = Object.fromEntries(
        Object.entries(filledData).map(([key, val]) => [key, val?.id ?? val])
      );
      const attachData = Object.fromEntries(
        Object.entries(filledData).map(([key, val]) => [key, val?.name ?? val])
      );
      // 🧹 STEP 4: REMOVE UI / TABLE ONLY FIELDS
      const cleanPayload = Object.fromEntries(
        Object.entries(updatedData).filter(
          ([key]) =>
            !["id", "selectedId", "selected", "rowIndex", "__index"].includes(
              key
            )
        )
      );

      // 🟡 Loading control
      type === "table" ? setLoading(true) : setSaveLoading(true);
      // 🧩 Resolve current config (form/tab)
      const resolveConfig = (items, id) => {
        for (const item of items) {
          if (item.id === id) return item;
          if (item.subTabs) {
            const found = resolveConfig(item.subTabs, id);
            if (found) return found;
          }
        }
        return null;
      };

      const activeItem = sidebarItems[activeIndex];
      const activeSubTab = activeItem.subTabs?.find(
        (subTab) => subTab.id === activeSubIndex
      );
      const activeNestedTab = activeSubTab?.subTabs?.[activeNestedIndex];
      const config = dialogOwnerConfig
        ? resolveConfig(sidebarItems, dialogOwnerConfig)
        : activeNestedTab || activeSubTab || activeItem;

      if (extraApi) {
        // Run your filter or extra API request
        const extraResponse = await extraHandleSave(
          extraApi,
          updatedData,
          config.id
        );
        const dataToStore = Array.isArray(extraResponse)
          ? extraResponse
          : [extraResponse];

        // 🗂️ Update formData (store under config.id or dialogOwnerConfig)
        setFormData((prev) => ({
          ...prev,
          [config.id]: dataToStore,
        }));

        // ✅ Show success dialog
        setSuccessDialogConfig({
          label: "Success",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {"Filtered data loaded successfully"}
            </strong>
          ),
          buttonText: "OK",
          IconComponent: LuCheck,
          status: "success",
        });
        setShowSuccessDialog(true);

        // Stop further execution
        return;
      }
      // 🟢 Inject processingId for "general" before sending
      if (config.id === "general" && formData?.general?.processingId) {
        updatedData.processingId = formData?.general?.processingId;
      }

      const currentActiveId =
        activeNestedTab?.id || activeSubTab?.id || activeItem?.id;

      if (!config?.api) {
        console.warn("ℹ️ No API configured for:", config?.label);
        return;
      }

      // 🧠 Determine IDs
      let importerId = importerMasterId;

      if (config.replaceMasterId) {
        const data = formData[config.replaceMasterId];

        if (Array.isArray(data) && data.length > 0) {
          const selectedItem = data.find((item) => item.selected);
          importerId = selectedItem ? selectedItem.id : data[0].id;
        } else if (data && typeof data === "object") {
          importerId = data.id;
        }
      }

      let tableImporterId;
      if (config.tableReplaceMasterId) {
        const tableData = formData[config.tableReplaceMasterId];

        if (Array.isArray(tableData) && tableData.length > 0) {
          const selectedItem = tableData.find((item) => item.selected);
          tableImporterId = selectedItem ? selectedItem.id : tableData[0].id;
        } else if (tableData && typeof tableData === "object") {
          tableImporterId = tableData.id;
        } else {
          // fallback to any form copy
          const tableFormData =
            formData[`${config.tableReplaceMasterId}Form`]?.[
              `${config.tableReplaceMasterId}Id`
            ];
          if (tableFormData) tableImporterId = tableFormData;
        }
      }

      let response;
      let tableResponse;
      const apiSet = apiHooks[config.api] || {};
      const { create, update } = apiSet;

      // ========== FORM + TABLE TYPE ==========
      if (config.type === "formWithTable") {
        const formAPI = apiHooks[config.api] || {};
        const tableAPI = apiHooks[config.tableApi] || {};
        const { create: createForm, update: updateForm } = formAPI;
        const { create: createTable, update: updateTable } = tableAPI;

        const formPayload = config.mapper?.(updatedData) || updatedData;
        const tablePayload = { ...updatedData };

        // 🟢 Handle Form
        if (type !== "table") {
          if (filledData.selectedId && updateForm && isEditing) {
            response = await updateForm({
              id: importerId,
              ...formPayload,
            }).unwrap();
          } else if (createForm) {
            response = await createForm({
              id: importerId,
              ...formPayload,
            }).unwrap();
          }

          const formId = response?.data || response?.id;
          if (formId) tablePayload.formId = formId;
        } else {
          // 🟢 Handle Table
          delete tablePayload.selectedId;
          if (filledData.selectedId && updateTable && isEditing) {
            tableResponse = await updateTable({
              id: tableImporterId,
              subId: filledData.selectedId,
              ...tablePayload,
            }).unwrap();
          } else if (createTable) {
            tableResponse = await createTable({
              id: tableImporterId,
              ...tablePayload,
            }).unwrap();
          }
        }
      }
      // ========== REGULAR FORM / TABLE ==========
      else {
        const id = filledData.selectedId;
        const data = config.mapper?.(updatedData) || updatedData || cleanPayload;

        let attachId = null;

        if (config.attachData) {
          const dataArray = formData[config.attachData] || [];

          if (Array.isArray(dataArray) && dataArray.length > 0) {
            const selectedItem = dataArray.find((item) => item.selected); // assuming `selected` flag
            attachId = selectedItem ? selectedItem.id : dataArray[0].id;
          }
        }

        const payload = {
          id: importerId,
          ...data,
          ...(attachId && { dataId: attachId }),
        };

        // return;
        delete payload.selectedId;
        if (!isCopyMode && id && update) {
          response = await update({ subId: id, ...payload }).unwrap();
        } else if (create) {
          if (config.id == "general" && importerMasterId) {
            response = await update({ ...payload }).unwrap();
          } else {
            response = await create(payload).unwrap();
            if (config.id == "general")
              setImporterMasterId(response.data), handlerefresh(response.data);
          }
        }
      }
      // 🟢 Update form data in state if needed
      if (!dialogOwnerConfig || dialogOwnerConfig === currentActiveId) {
        let rowData = null;
        let newRowId = null;

        if (config.type === "formWithTable" && tableResponse) {
          rowData = flattenDataTable(attachData);
          newRowId = tableResponse?.data?.id || tableResponse?.data;
        } else if (["table"].includes(config.type) && response) {
          rowData = flattenDataTable(attachData);
          newRowId = response?.data?.id || response?.data;
        }
      }

      handleCloseForm();
      // 🟢 Show Success Dialog
      const success =
        response?.data?.statusCode === 200 ||
        response?.statusCode === 200 ||
        tableResponse?.data?.statusCode === 200 ||
        tableResponse?.statusCode === 200;
      setSuccessDialogConfig({
        label: success ? "Success" : "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {response?.data?.message ||
              response?.message ||
              "Data save successfully"}
          </strong>
        ),
        buttonText: "OK",
        IconComponent: success ? LuCheck : BiSolidError,
        status: success ? "success" : "error",
      });
      setShowSuccessDialog(true);
      try {
        // Clear filledData by resetting the form
        if (formRef.current && formRef.current.reset) {
          formRef.current.reset(); // This should clear all form fields
        }

        setFormDialogData({}); // Clear any dialog data
        setIsEditing(false); // Reset editing state
      } catch (resetError) {
        console.warn("⚠️ Form reset failed:", resetError);
      }

      return { response, tableResponse };
    } catch (err) {
      console.error("❌ Save failed:", err);
      setSuccessDialogConfig({
        label: "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {err?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        icon: BiSolidError,
      });
      setShowSuccessDialog(true);
      throw err;
    } finally {
      if (!extraApi) {
        setLoading(false);
        setSaveLoading(false);
        setImportTrigger((prev) => prev + 1);
      }
      setExtraApi(null);
    }
  };

  const renderButtons = (
    subButtons = [],
    subcheckBox = [],
    schema,
    label,
    type
  ) => {
    if (!subButtons.length && !subcheckBox.length) return null;

    return (
      <Box
        display="flex"
        justifyContent={subcheckBox.length > 0 ? "space-between" : "flex-end"}
        alignItems="center"
        mb={2}
      >
        {subcheckBox.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={1} px={3}>
            {subcheckBox.map((item, idx) => (
              <CheckboxBox
                key={idx}
                checkboxLabel={item.label}
                value={[item.label] || false}
                onChange={(val) => handleCheckboxChange(item.label, val)}
              />
            ))}
          </Box>
        )}

        {subButtons.length > 0 && (
          <Box display="flex" gap={1}>
            {subButtons.map((btn, idx) => {
              const Icon = iconMap[btn.icon];
              return (
                <OutLinedButton
                  key={idx}
                  label={btn.label}
                  startIcon={Icon ? <Icon /> : null}
                  onClick={() => {
                    if (btn.onClick === "openForm") {
                      openForm(schema, label);
                    } else if (typeof btn.onClick === "function") {
                      btn.onClick();
                    }
                  }}
                  sx={{ border: "none" }}
                />
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  const handleChange = () => {};

  const tableSchema = currentTab?.data
    ?.filter((item) => item.type === "table")
    ?.map((item) => item.schema);

  const handleActions = (payload) => {
    const { action, data } = payload;
    console.log(payload, "payload");
    switch (action) {
      case "openDialog":
        openForm(data?.schema, "test");
        break;
      case "openSubDialog":
        const ComponentToRender = dynamicComponentMap[data.subComponent];
        if (!ComponentToRender) {
          console.warn(`⚠️ No component found for ${data.subComponent}`);
          return;
        }
        setSubDialogConfig({
          Component: ComponentToRender,
          props: data.props || {},
          data,
        });
        // setShowForm(false);
        setSubDialogOpen(true);

        break;
      case "openbuttonsubdialog":
        const subBtn = data?.buttons?.find((btn) => btn.subComponent);
        const ComponentRender = subBtn
          ? dynamicComponentMap[subBtn.subComponent]
          : null;

        if (!ComponentRender) {
          console.warn(
            `⚠️ No component found for ${subBtn?.subComponent || "unknown subComponent"}`
          );
          return;
        }

        setSubDialogConfig({
          Component: ComponentRender,
          props: data.props || {},
          data,
        });
        // setShowForm(false);
        setSubDialogOpen(true);
        break;

      case "delete":
        break;

      case "reset":
        break;

        case "copyrow": {
          if (!selectedRowData) return;
        
          const copyData = cleanCopyData(selectedRowData);
        console.log("📝 [handleActions - copyrow] copyData:", copyData) ;
          setIsEditing(false);
          setIsCopyMode(true);
        
          setFormDialogData(copyData); // ✅ PERFECT DATA
          setShowForm(true);
        
          setSchema({
            schema: data.schema,
            label: data.label,
          });
        
          return;
        }
        

      case "refresh":
        handleRefresh(data?.api);
        break;

      case "navigate":
        navigate(data?.navigateTo);
        break;

      // ✅ This is the fix: use data.filename
      case "openFile": {
        const safePayload = {
          type,
          action,
          data, // pass full data object
          filename, // pass filename fallback
        };
        handleSubmit(safePayload);
        break;
      }
      case "active":
        setAnchorEl1(true);
        setOpenComponent({ component: data });
        dynamicComponentMap[data];
        break;
      default:
        console.warn("Unknown action:", action);
    }
  };

  const handleSubmit = ({ type, action, data, filename }) => {
    if (action === "openFile") {
      const targetFile =
        componentMap[currentActiveId] ||
        data?.filename?.trim() ||
        filename?.trim();

      if (componentMap[targetFile]) {
        setActiveFile(targetFile);
        setOpeningForm(false);
      } else {
        console.warn("❌ No component found for", targetFile);
      }
    }
  };

  const handleRefresh = async (api) => {
    if (!importerMasterId) return;
    try {
      setSaveLoading(true);
      const { fetch } = apiHooks[api] || {};
      if (!fetch) {
        console.warn(`No fetch method found for API: ${api}`);
        return;
      }
      const response = await fetch({ id: importerMasterId });
      setFormData((prev) => ({
        ...prev,
        [currentTab.id]: response,
      }));
    } catch (err) {
      console.error("Refresh error:", err);
    } finally {
      setSaveLoading(false);
    }
  };

  const subTabs = useMemo(
    () =>
      activeItem.subTabs?.map((tab) => ({
        id: tab.id,
        label: tab.label,
        disabled: tab.disabled,
      })) || [],
    [activeItem]
  );
  const dialogWidth = currentTab?.id === "shipment" ? 1020 : 800;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",

        gap: "8px",
        overflow: "hidden",
        height: "100vh",
        maxWidth: "100%",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        flexShrink={0}
      >
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            color: "var(--brand-910)",
            fontSize: "13px",
            cursor: "pointer",
            gap: 1,
          }}
        >
          <Typography variant="heading">Create New Job</Typography>
          <Box
            display="flex"
            alignItems="center"
            variant="bodySmallStrong"
            gap={1}
            sx={{
              color: "var(--link-blue)",
              cursor: "pointer",
            }}
            onClick={() => {
              // localStorage.removeItem("exporterMasterId"); // ✅ clear when back is clicked
              navigate(navurl.ImportDashboard);
              setDisableCopy(false);
              localStorage.removeItem("copiedJobData");
            }}
          >
            <LuArrowLeft style={{ color: "var(--brand-600)", fontSize: 20 }} />
            Back to Import Dashboard
          </Box>
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="flex-end"
          gap={2}
          sx={{ flex: 1, flexWrap: "nowrap" }}
        >
          <IconButton
            onClick={handlerefresh}
            sx={{
              border: "1px solid var(--brand-600)",
              color: "var(--brand-600)",
              borderRadius: 2,
              backgroundColor: "var(--gray-0)",
              height: 40,
              width: 36,
              p: 0,
              "&:hover": {
                backgroundColor: "var(--gray-0)",
              },
            }}
          >
            <LuRefreshCw size={20} />
          </IconButton>
          <OutLinedButton
            label={isActivation && isEditMode ? "Active" : "In Active"}
            variant="bodySmallMedium"
            onClick={() =>
              handleActions({
                action: "active",
                data: "ActiveDialog",
              })
            }
            sx={{
              color:
                isActivation && isEditMode
                  ? "var(--green-600)"
                  : "var(--gray-400)",
              px: 2,
              border:
                isActivation && isEditMode
                  ? "1px solid var(--green-600)"
                  : "1px solid var(--gray-400)",
              borderRadius: 2,
              // fontWeight: 500,
              textTransform: "none",
              backgroundColor: "var(--gray-0)",
              "&:hover": {
                backgroundColor: "var(--gray-0)",
              },
              // fontSize: "14px",
            }}
          />

          <OutLinedButton
            label="Standard Documents"
            variant="bodySmallMedium"
            startIcon={<FiFileText />}
            endIcon={<LuChevronDown />}
            onClick={handleClick}
            sx={{
              color: "var(--brand-600)",
              px: 2,
              border: "1px solid var(--brand-600)",
              borderRadius: 2,
              // fontWeight: 500,
              textTransform: "none",
              backgroundColor: "var(--gray-0)",
              "&:hover": {
                backgroundColor: "var(--gray-0)",
              },
              // fontSize: "14px",
            }}
          />

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                maxHeight: 225, // 👈 reduce the dropdown height here
                width: 232,
                overflowY: "auto", // 👈 scroll when items exceed height
                borderRadius: 2,
              },
            }}
            MenuListProps={{
              sx: {
                py: 0, // 👈 optional: remove extra padding from the list
              },
            }}
          >
            {" "}
            {documentOptions.map((doc, idx) => (
              <MenuItem
                key={idx}
                onClick={() => handleSelect(doc)}
                sx={{
                  fontSize: "14px",
                  py: 1,
                  width: 232,
                }}
              >
                {doc}
              </MenuItem>
            ))}
          </Menu>

          <OutLinedButton
            label="Copy Job"
            variant="bodySmallMedium"
            startIcon={<LuCopy />}
            onClick={copyJobData} // ✅ separate function
            disabled={disableCopy} // ✅ use the state here
            sx={{
              color: "var(--brand-600)",
              px: 2,
              border: "1px solid var(--brand-600)",
              borderRadius: 2,
              textTransform: "none",
              backgroundColor: "var(--gray-0)",
              "&:hover": {
                backgroundColor: "var(--gray-0)",
              },
            }}
          />

          <>
            {/* Your existing button */}
            <OutLinedButton
              label="Import File"
              variant="bodySmallMedium"
              startIcon={<LuFileMinus />}
              sx={{
                color: "var(--brand-600)",
                px: 2,
                border: "1px solid var(--brand-600)",
                borderRadius: 2,
                textTransform: "none",
                backgroundColor: "var(--gray-0)",
                "&:hover": { backgroundColor: "var(--gray-0)" },
              }}
              onClick={handleImportClick} // triggers hidden file input
            />

            {/* Hidden file input */}
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
          </>

          <BlackButton
            label="Add New Job"
            variant="bodySmallMedium"
            startIcon={<LuPlus size={21} />}
            sx={{
              width: "170px",
              mt: 1,
              color: "var(--gray-0)",
              borderRadius: 2,
              textTransform: "none",
              backgroundColor: "var(--brand-600)",
            }}
            onClick={goToCreateNewJob}
          />
        </Box>
      </Box>

      {/* Render the   Filter Box  and  */}
      <Box
        sx={{
          flexShrink: 0,
          width: "100%",
          backgroundColor: "var(--gray-0)",
          borderRadius: 2,
          border: "1px solid var(--gray-100)",
        }}
      >
        {/* used fromData state to handle the header menu, instead if using separate state */}
        <CreateFilterBox
          handleChange={handleChange}
          showAdvancedFilters={showAdvancedFilters}
          setShowAdvancedFilters={setShowAdvancedFilters}
          data={formData["general"]}
          importerMasterId={importerMasterId}
        />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          backgroundColor: "var(--gray-0)",
          width: "100%",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CreateJobLayout
          // disabled={!exporterMasterId && activeIndex !== 0} // ✅
          activeIndex={activeIndex}
          setActiveIndex={(index) => {
            // ✅ prevent clicking Product tab if disabledData is false
            setActiveIndex(index);
          }}
          sx={{
            width: "100%",
            maxWidth: "90%",
            mx: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box key={activeIndex} sx={{ width: "100%", mb: 2 }}>
            <SectionHeader
              title={`${activeItem.label}`}
              buttons={[
                ...(activeItem?.buttons?.map((btn) => {
                  const Icon = iconMap[btn.icon];
                  return {
                    ...btn,
                    icon: Icon ? <Icon /> : null,
                    onClick: (event) => {
                      if (btn.action === "openForm") {
                        const schema = btn.schema || activeItem.schema;
                        const label = activeItem.label;
                        const extraApi = btn.extraApi;
                        openForm(schema, label, btn.ownerConfig, extraApi);
                      } else {
                        if (btn.onClick) {
                          // 🔹 Custom click exists → call it
                          btn.onClick(event, btn);
                        } else {
                          // 🔹 No custom click → fallback to handleActions
                          handleActions({
                            type: activeItem.type,
                            action: btn.action.toLowerCase(),
                            data: activeItem,
                          });
                        }
                      }
                    },
                  };
                }) || []),
                ...(activeItem.type === "tab"
                  ? activeSubTab?.buttons?.map((btn) => {
                      const Icon = iconMap[btn.icon];
                      return {
                        ...btn,
                        icon: Icon ? <Icon /> : null,
                        onClick: (event) => {
                          if (btn.action === "openForm") {
                            const schema = btn.schema || activeSubTab.schema;
                            const label = activeSubTab.label;
                            openForm(schema, label);
                          } else {
                            if (btn.onClick) {
                              // 🔹 Custom click exists → call it
                              btn.onClick(event, btn);
                            } else {
                              // 🔹 No custom click → fallback to handleActions
                              handleActions({
                                type: activeSubTab.type,
                                action: btn.action,
                                data: activeSubTab,
                              });
                            }
                          }
                        },
                      };
                    }) || []
                  : []),
              ]}
              titleButtons={[
                ...(activeItem?.titleButtons?.map((btn) => {
                  const Icon = iconMap[btn.icon];
                  return {
                    ...btn,
                    icon: Icon ? <Icon /> : null,
                    onClick: () => {
                      if (btn.action === "openForm") {
                        openForm(activeItem.schema, activeItem.label);
                      } else {
                        const fn = functionMap[btn.onClick];
                        if (typeof fn === "function") {
                          fn(event, btn); // pass event if needed
                        }
                        // btn.onClick?.();
                      }
                    },
                  };
                }) || []),

                ...(activeItem.type === "tab"
                  ? activeSubTab?.titleButtons?.map((btn) => {
                      const Icon = iconMap[btn.icon];
                      return {
                        ...btn,
                        icon: Icon ? <Icon /> : null,
                        onClick: () => {
                          if (btn.action === "openForm") {
                            const schema = btn.schema || activeSubTab.schema;
                            const label = btn.label || activeSubTab.label;
                            openForm(schema, label);
                          } else {
                            btn.onClick?.();
                          }
                        },
                      };
                    }) || []
                  : []),
              ]}
            />

            <Box>
              {activeItem?.type === "form" && (
                <Box sx={{ width: "100%", px: 2, py: 1 }}>
                  {/* For all form generator, pass the compkey, so that formGenerator will display the specific tab's initialvalue and pass the formRef, so that when submit the updated values will receive in the handleSave function*/}
                  <FormGenerator
                    compKey={activeItem.id}
                    key={activeItem.id}
                    schema={activeItem.schema}
                    onSubmit={handleActions}
                    ref={formRef}
                    onRequiredFieldsChange={(allFilled) =>
                      setIsRequiredFieldFilled(allFilled)
                    } // ✅ pass this
                  />
                </Box>
              )}

              {activeItem?.type === "table" && (
                <Box>
                  {/* For all table generator, pass the compkey, so that tableGenerator will display the specific tab's initialvalue and pass the formRef, so that when submit the updated values will receive in the handleSave function*/}
                  <TableGenerator
                    handleSave={handleSave}
                    handleDelete={handleDelete}
                    label={activeItem.label}
                    ref={formRef}
                    compKey={activeItem.id}
                    key={activeItem.id}
                    handlers={handlers}
                    visibleFields={activeItem.visibleFields}
                    expandedFields={activeItem.expandedFields}
                    actions={activeItem?.actions || null}
                    loading={saveLoading}
                    onSubmit={handleActions}
                  />
                </Box>
              )}

              {activeItem?.type === "tableWithFields" && (
                <Box>
                  {/* For all table with field generator, pass the compkey, so that TableWithFieldGenerator will display the specific tab's initialvalue and pass the formRef, so that when submit the updated values will receive in the handleSave function*/}
                  <TableWithFieldGenerator
                    compKey={activeItem.id}
                    key={activeItem.id}
                    handleSave={handleSave}
                    handleDelete={handleDelete}
                    label={activeItem.label}
                    ref={formRef}
                    groupedHeaders={activeItem.groupedHeaders}
                    visibleFields={activeItem.visibleFields}
                    expandedFields={activeItem.expandedFields}
                    actions={activeItem?.actions}
                    tableSx={activeItem?.sx}
                  />
                </Box>
              )}

              {activeItem?.type === "formWithTable " && (
                <Box>
                  <FormGenerator
                    compKey={activeItem.id}
                    key={activeItem.id}
                    schema={activeItem.formSchema}
                    onSubmit={handleActions}
                    ref={formRef}
                    onRequiredFieldsChange={(allFilled) =>
                      setIsRequiredFieldFilled(allFilled)
                    } // ✅ pass this
                  />
                  <TableGenerator
                    compKey={activeItem.id}
                    key={activeItem.id}
                    handleSave={handleSave}
                    handleDelete={handleDelete}
                    label={activeItem.label}
                    ref={formRef}
                    handlers={handlers}
                    visibleFields={activeItem.visibleFields}
                    expandedFields={activeItem.expandedFields}
                    actions={activeItem?.actions || null}
                  />
                </Box>
              )}

              {/* Render custom component if provided */}
              {activeItem.component &&
                (() => {
                  const Component = activeItem.component;
                  return (
                    <Component
                      {...activeItem.componentProps}
                      formData={formData}
                      setFormData={setFormData}
                      ref={formRef}
                      handleSave={handleSave}
                      handleDelete={handleDelete}
                      handleRowSelect={handleRowSelect}
                    />
                  );
                })()}

              {activeItem?.type === "tab" && (
                <Box>
                  <CustomTabs
                    tabs={subTabs}
                    activeTab={activeSubIndex}
                    setActiveTab={setActiveSubIndex}
                  />
                  {/* Checkboxes (if any) */}
                  {activeSubTab?.checkBox?.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={1} px={3}>
                      {activeSubTab.checkBox.map((item, idx) => (
                        <CheckboxBox
                          key={idx}
                          checkboxLabel={item.label}
                          value={[item.label] || false} // use your state
                          onChange={(val) =>
                            handleCheckboxChange(item.label, val)
                          }
                        />
                      ))}
                    </Box>
                  )}
                  {/* Render custom component if provided */}
                  {activeSubTab?.component &&
                    (() => {
                      const Component = activeSubTab.component;
                      return (
                        <Component
                          formData={formData}
                          setFormData={setFormData}
                          ref={formRef}
                          handleSave={handleSave}
                          handleDelete={handleDelete}
                          handleRowSelect={handleRowSelect}
                        />
                      );
                    })()}

                  <Box>
                    {activeSubTab?.type === "form" && activeSubTab.schema && (
                      <Box
                        mt={2}
                        px={4}
                        display="flex"
                        flexDirection="column"
                        gap={2}
                      >
                        {/* Form */}
                        <FormGenerator
                          compKey={activeSubTab.id}
                          key={activeSubTab.id}
                          schema={activeSubTab.schema}
                          onSubmit={handleActions}
                          ref={formRef}
                          onRequiredFieldsChange={(allFilled) =>
                            setIsRequiredFieldFilled(allFilled)
                          } // ✅ pass this
                        />
                      </Box>
                    )}
                    {/* Render current sub-tab's table */}
                    {activeSubTab?.type === "table" && (
                      <>
                        {(activeSubTab?.subcheckBox ||
                          activeSubTab?.subButtons) && (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            gap={2}
                            mb={2}
                            ml={2} // optional left padding
                          >
                            {/* Left: Checkboxes */}
                            {activeSubTab?.subcheckBox?.length > 0 && (
                              <Box display="flex" flexWrap="wrap" gap={1}>
                                {activeSubTab.subcheckBox.map((item, idx) => (
                                  <CheckboxBox
                                    key={idx}
                                    checkboxLabel={item.label}
                                    value={[item.label] || false}
                                    onChange={(val) =>
                                      handleCheckboxChange(item.label, val)
                                    }
                                  />
                                ))}
                              </Box>
                            )}

                            {/* Right: SubButtons always flex-end */}
                            {/* {activeSubTab?.subButtons?.length > 0 && (
                              <Box
                                display="flex"
                                flexWrap="wrap"
                                justifyContent="flex-end"
                                gap={1}
                                flexGrow={1}
                              >
                                {activeSubTab.subButtons.map((btn, idx) => {
                                  const Icon = iconMap[btn.icon];
                                  return (
                                    <OutLinedButton
                                      key={idx}
                                      checkboxLabel={item.label}
                                      value={[item.label] || false}
                                      onChange={(val) =>
                                        handleCheckboxChange(item.label, val)
                                      }
                                    />
                                  ))}
                                </Box>
                              )} */}

                            {/* Right: SubButtons always flex-end */}
                            {activeSubTab?.subButtons?.length > 0 && (
                              <Box
                                display="flex"
                                flexWrap="wrap"
                                justifyContent="flex-end"
                                gap={1}
                                flexGrow={1}
                              >
                                {activeSubTab.subButtons.map((btn, idx) => {
                                  const Icon = iconMap[btn.icon];
                                  return (
                                    <OutLinedButton
                                      key={idx}
                                      label={btn.label}
                                      startIcon={Icon ? <Icon /> : null}
                                      onClick={() => {
                                        if (btn.action === "openForm") {
                                          openForm(
                                            activeSubTab.schema,
                                            activeSubTab.label
                                          );
                                        } else if (
                                          typeof btn.onClick === "function"
                                        ) {
                                          btn.onClick();
                                        }
                                      }}
                                      sx={{
                                        mr: 1,
                                        mb: 1,
                                        border: "none",
                                        ...(btn.sx || {}),
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                        )}

                        <TableGenerator
                          compKey={activeSubTab.id}
                          key={activeSubTab.id}
                          handleSave={handleSave}
                          handleDelete={handleDelete}
                          visibleFields={activeSubTab.visibleFields}
                          expandedFields={activeSubTab.expandedFields}
                          label={activeSubTab.label}
                          ref={formRef}
                          handlers={handlers}
                          actions={activeSubTab?.actions || null}
                          onSubmit={handleActions} // ✅ VERY IMPORTANT
                        />
                      </>
                    )}

                    {activeSubTab?.type === "tableWithFields" && (
                      <TableWithFieldGenerator
                        compKey={activeSubTab.id}
                        key={activeSubTab.id}
                        handleSave={handleSave}
                        handleDelete={handleDelete}
                        label={activeSubTab.label}
                        ref={formRef}
                        groupedHeaders={activeSubTab.groupedHeaders}
                        visibleFields={activeSubTab.visibleFields}
                        expandedFields={activeSubTab.expandedFields}
                        actions={activeSubTab?.actions}
                        tableSx={activeSubTab?.sx}
                      />
                    )}
                    {activeSubTab?.type === "formWithTable" && (
                      <Box>
                        {activeSubTab?.checkBox?.length > 0 && (
                          <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={1}
                            mb={2}
                            px={3}
                          ></Box>
                        )}

                        {activeSubTab.data?.map((section, idx) => {
                          if (section.type === "form") {
                            return (
                              <Box key={idx} mt={2} px={4}>
                                <FormGenerator
                                  compKey={activeSubTab.id + "Form"}
                                  key={activeSubTab.id + "Form"}
                                  schema={section.schema}
                                  onSubmit={handleActions}
                                  ref={formRef}
                                  onRequiredFieldsChange={(allFilled) =>
                                    setIsRequiredFieldFilled(allFilled)
                                  } // ✅ pass this
                                />
                              </Box>
                            );
                          }

                          if (section.type === "table") {
                            return (
                              <Box>
                                {renderButtons(
                                  section?.subButtons,
                                  section?.subcheckBox, // <-- pass checkboxes here
                                  section?.schema,
                                  section?.label,
                                  activeSubTab?.type === "formWithTable"
                                )}
                                <TableGenerator
                                  compKey={activeSubTab.id}
                                  key={activeSubTab.id}
                                  handleSave={handleSave}
                                  handleDelete={handleDelete}
                                  visibleFields={section.visibleFields}
                                  expandedFields={section.expandedFields}
                                  label={activeSubTab.label}
                                  ref={formRef}
                                  handlers={handlers}
                                  actions={section?.actions || null}
                                  onSubmit={handleActions}
                                />
                              </Box>
                            );
                          }

                          return null; // default
                        })}
                      </Box>
                    )}

                    {activeSubTab?.type === "tab" && (
                      <>
                        <TabWithIndependentButtons
                          tabs={activeSubTab.subTabs}
                          buttons={(
                            activeSubTab.subTabs[activeNestedIndex]?.buttons ||
                            []
                          ).map((btn) => {
                            const Icon = iconMap[btn.icon];
                            return {
                              ...btn,
                              startAdornment: Icon ? <Icon /> : null,
                              sx: { border: "none", ...(btn.sx || {}) },
                              onClick: () => {
                                if (btn.onClick === "openForm") {
                                  // ✅ pass schema from button itself
                                  openForm(
                                    activeSubTab.subTabs[activeNestedIndex]
                                      .schema,
                                    activeSubTab.subTabs[activeNestedIndex]
                                      .label
                                  );
                                }
                              },
                            };
                          })}
                          activeTab={activeNestedIndex}
                          setActiveTab={setActiveNestedIndex}
                        />

                        <Box mt={2}>
                          {(() => {
                            const nestedTab =
                              activeSubTab.subTabs[activeNestedIndex || 0];

                            if (
                              nestedTab?.type === "form" &&
                              nestedTab.schema
                            ) {
                              return (
                                <Box mt={2} px={4}>
                                  <FormGenerator
                                    compKey={nestedTab.id}
                                    key={nestedTab.id}
                                    schema={nestedTab.schema}
                                    onSubmit={handleActions}
                                    ref={formRef}
                                    onRequiredFieldsChange={(allFilled) =>
                                      setIsRequiredFieldFilled(allFilled)
                                    } // ✅ pass this
                                  />
                                </Box>
                              );
                            }

                            if (nestedTab?.type === "formWithTable") {
                              return (
                                <Box>
                                  {nestedTab?.checkBox?.length > 0 && (
                                    <Box
                                      display="flex"
                                      flexWrap="wrap"
                                      gap={1}
                                      mb={2}
                                      px={3}
                                    ></Box>
                                  )}

                                  {nestedTab.data?.map((section, idx) => {
                                    if (section.type === "form") {
                                      return (
                                        <Box key={idx} mt={2} px={4}>
                                          <FormGenerator
                                            compKey={nestedTab.id + "Form"}
                                            key={nestedTab.id + "Form"}
                                            schema={section.schema}
                                            onSubmit={handleActions}
                                            ref={formRef}
                                            onRequiredFieldsChange={(
                                              allFilled
                                            ) =>
                                              setIsRequiredFieldFilled(
                                                allFilled
                                              )
                                            } // ✅ pass this
                                          />
                                        </Box>
                                      );
                                    }

                                    if (section.type === "table") {
                                      return (
                                        <Box>
                                          {renderButtons(
                                            section?.subButtons,
                                            section?.subcheckBox, // <-- pass checkboxes here
                                            section?.schema,
                                            section?.label,
                                            nestedTab?.type === "formWithTable"
                                          )}
                                          <TableGenerator
                                            compKey={nestedTab.id}
                                            key={nestedTab.id}
                                            handleSave={handleSave}
                                            handleDelete={handleDelete}
                                            visibleFields={
                                              section.visibleFields
                                            }
                                            expandedFields={
                                              section.expandedFields
                                            }
                                            label={nestedTab.label}
                                            ref={formRef}
                                            handlers={handlers}
                                            actions={section?.actions || null}
                                            onSubmit={handleActions}
                                          />
                                        </Box>
                                      );
                                    }

                                    return null; // default
                                  })}
                                </Box>
                              );
                            }

                            if (nestedTab?.type === "table") {
                              return (
                                <TableGenerator
                                  compKey={nestedTab.id}
                                  key={nestedTab.id}
                                  handleSave={handleSave}
                                  handlers={handlers}
                                  handleDelete={handleDelete}
                                  visibleFields={nestedTab.visibleFields}
                                  expandedFields={nestedTab.expandedFields}
                                  label={nestedTab.label}
                                  ref={formRef}
                                  actions={nestedTab?.actions || null}
                                />
                              );
                            }

                            return null;
                          })()}
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </CreateJobLayout>
      </Box>
      {/* </Box> */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          bottom: 0,
        }}
      >
        {/* Left Button: Declarations */}
        <OutLinedButton
          variant={"bodySmallStrong"}
          label="Declarations"
          sx={{
            width: "150px",
            height: "45px",

            textTransform: "none",
            border: "1px solid var(--brand-600)",
            borderRadius: "8px",
          }}
          width="180px"
          height="40px"
        />
        {/* Right Side Buttons */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <OutLinedButton
            variant={"bodySmallStrong"}
            label="Close"
            onClick={() => {
              // localStorage.removeItem("exporterMasterId"); // ✅ clear when back is clicked
              localStorage.removeItem("filterData");
              navigate(navurl.ImportDashboard);
            }}
            sx={{
              fontSize: "13px",
              width: "120px",
              height: "40px",
              fontWeight: 600,
              textTransform: "none",
              border: "1px solid var(--brand-600)",
              borderRadius: "8px",
            }}
          />

          <BlackButton
            label={saveLoading ? "Saving..." : "Save"}
            sx={{
              width: "120px",
              height: "40px",
              fontSize: "13px",
              fontWeight: 500,
              textTransform: "none",
              boxShadow: "none",
              backgroundColor: "var(--brand-600)",
              color: "var(--gray-0)",
              border: "none",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "var(--brand-600)",
                boxshadow: "none",
              },
              mb: 0,
            }}
            onClick={!saveLoading && handleSave}
            disabled={
              !isRequiredFieldFilled ||
              saveLoading ||
              currentTab?.type === "table"
            }
          />
        </Box>
      </Box>
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
        }}
        {...successDialogConfig}
      />

      {showForm && (
        <FormDialog
          title={
            isEditing
              ? `Edit ${schema?.label ?? currentTab.label}`
              : `Add ${schema?.label ?? currentTab.label}`
          }
          schema={schema?.schema ?? currentTab.schema ?? tableSchema[0]}
          initialData={formDialogData}
          isEditing={isEditing}
          onSubmit={() => handleSave("table")}
          handleActions={handleActions}
          onClose={handleCloseForm}
          showCancelButton={true}
          showSubmitButton={true}
          cancelLabel="Cancel"
          submitLabel={isEditing ? "Save" : "Add"}
          loading={loading}
          ref={formRef}
          disabled={!isRequiredFieldFilled}
          dialogWidth={dialogWidth} // ✅ no more "not defined" error
        />
      )}

      {subDialogConfig && (
        <Dialog
          open={subDialogOpen}
          onClose={() => setSubDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <subDialogConfig.Component
            {...subDialogConfig.props}
            onClose={(value) => {
              // ✅ Only SpecialPermission sends value
              if (subDialogConfig?.data?.subComponent === "SpecialPermission") {
                console.log("✅ Special Permission Data:", value);

                const finalValue = value.join(", ");

                setFormData((prev) => ({
                  ...prev,
                  general: {
                    ...prev.general, // ✅ keep existing general fields
                    importerGeneralSpecialPermission: finalValue,
                  },
                }));

                // ✅ Update RHF Field
                setValueGlobal("importerGeneralSpecialPermission", finalValue);
              }

              // ✅ ALL dialogs — normal closeta
              setSubDialogOpen(false);
            }}
          />
        </Dialog>
      )}

      {openComponent &&
        (() => {
          const DynamicComponent = dynamicComponentMap[openComponent.component];
          if (!DynamicComponent) return null;

          return (
            <DynamicComponent
              open={Boolean(anchorEl1)}
              anchorEl={anchorEl1}
              onClose={functionMap.handleClose}
              setShowSuccessDialog={setShowSuccessDialog}
              setSuccessDialogConfig={setSuccessDialogConfig}
              {...openComponent.props}
            />
          );
        })()}
    </Box>
  );
};

export default React.memo(CreateNewJob);
